//
//  client.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  The one API client for both console apps: versioned /v1 requests with the
//  bearer token passed in, so no session state lives here. Tracks the assumed
//  session header (RB-13) and the wrong_plane flag. Never sends X-Tenant.
//
import { parseEnvelope, type Envelope } from './envelope'

// One client for the versioned API. Human session credentials only -- the console never holds a
// machine credential (06 §6). The token is passed by the caller (auth.ts) so this module stays
// free of session state.
//
// `X-Tenant` is gone (27 §8): a principal's plane and tenant come from its token, not a header the
// client sets. A platform operator who needs to look at a tenant goes through
// `POST /v1/platform/assume`, which mints a tenant-plane token for the tenant app to use -- see
// the platform app's Assume page and the tenant app's assumed banner.
export const API_VERSION = 'v1'

export type ApiOptions = RequestInit

let onPasswordChangeRequired: (() => void) | null = null
export function setPasswordChangeRequiredHandler(fn: (() => void) | null) {
  onPasswordChangeRequired = fn
}

let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn
}

// A platform operator "assumes" a tenant (`POST /v1/platform/assume`, 27 §8/§8a): every response
// under that session carries `X-Signet-Assumed: <email>; mode=<m>; expires=<ts>`. The tenant app's
// banner (never dismissible, RB-13 Part D) reads this store. Parsed here, once, so every page's
// fetch keeps it current without plumbing a header through every call site.
export type Assumed = { email: string; mode: 'read' | 'write'; expiresAt: number } | null
let assumed: Assumed = null
const assumedListeners = new Set<() => void>()
export function getAssumed(): Assumed {
  return assumed
}
export function subscribeAssumed(l: () => void) {
  assumedListeners.add(l)
  return () => assumedListeners.delete(l)
}
function setAssumed(next: Assumed) {
  assumed = next
  assumedListeners.forEach((l) => l())
}
function parseAssumedHeader(raw: string | null): Assumed {
  if (!raw) return null
  const parts = Object.fromEntries(
    raw.split(';').map((p) => p.trim().split('=')).filter((kv) => kv.length === 2) as [string, string][],
  )
  const email = raw.split(';')[0]?.trim()
  const mode = parts.mode === 'write' ? 'write' : 'read'
  const expiresAt = Number(parts.expires) || 0
  return email ? { email, mode, expiresAt } : null
}

// §1/§6g's three RB-13 error codes: `wrong_plane`, `assumed_read_only`, `x_tenant_removed`.
// `assumed_read_only` (a `mode=read` assumed session hit a mutating route) and `x_tenant_removed`
// (nobody should ever send `X-Tenant` from this client -- it's gone) both carry a human-readable
// `message` from the server and need no special UI: every page already shows `error.message` on a
// failed call. `wrong_plane` is different -- it means this whole session doesn't belong in this
// app, not just this one request -- so it gets a dedicated reactive store below and a full-screen
// takeover (`WrongPlane`), not an inline error.
//
// A role from the other plane gets `403 wrong_plane` no matter its rank, checked before role. The
// console's own plane check (`isPlatformRole`/`isTenantRole` at login) is the fast path; this store
// is the backstop for any route it missed -- both apps' `App` renders `WrongPlane` off it, not just
// off the local role guess.
let wrongPlane = false
const wrongPlaneListeners = new Set<() => void>()
export function getWrongPlane(): boolean {
  return wrongPlane
}
export function subscribeWrongPlane(l: () => void) {
  wrongPlaneListeners.add(l)
  return () => wrongPlaneListeners.delete(l)
}
function setWrongPlane(next: boolean) {
  if (next === wrongPlane) return
  wrongPlane = next
  wrongPlaneListeners.forEach((l) => l())
}

export async function api<T>(path: string, init: ApiOptions = {}, token?: string | null): Promise<Envelope<T>> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)
  try {
    const res = await fetch(`/${API_VERSION}${path}`, { ...init, headers })
    setAssumed(parseAssumedHeader(res.headers?.get('X-Signet-Assumed') ?? null))
    const env = parseEnvelope<T>(await res.json())
    if (res.status === 401 && token && onUnauthorized) onUnauthorized()
    if (!env.success && env.error.code === 'wrong_plane') setWrongPlane(true)
    if (!env.success && env.error.code === 'password_change_required' && token && onPasswordChangeRequired) onPasswordChangeRequired()
    return env
  } catch (e) {
    return { success: false, error: { code: 'network_error', message: e instanceof Error ? e.message : String(e) } }
  }
}
